import {
  ArrowBack,
  Person,
  ShoppingBag,
  Analytics,
  Email,
  WorkOutline,
  VerifiedUser,
} from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

import Loading from '../../../../components/global/loading/Loading';
import OrderCard from '../../../../components/global/orderCard/OrderCard';
import { useUser } from '../../../../context/UserContext';
import translateUserStatus from '../../../../helperFunctions/translateUserStatus';
import { User as UserModel, UserStatus } from '../../../../models/User';

// Chart imports
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

function User() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = React.useState<UserModel | null>(null);
  const navigate = useNavigate();
  const { getUser } = useUser();
  const location = useLocation();

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  const getUserData = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      const userData = await getUser(id);
      setUser(userData);
    } catch (error: any) {
      toast.error(error.message || 'Wystąpił błąd podczas pobierania danych');
      navigate(-1);
    }
  }, [getUser, id, navigate]);

  const handleBackClick = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 1) {
      const newPath = '/' + pathParts.slice(0, -1).join('/');
      navigate(newPath);
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    if (!id) {
      toast.error('Brak ID użytkownika');
      navigate(-1);
      return;
    }
    getUserData();
  }, [getUserData, id, navigate]);

  // Calculate seller-related metrics only after user data is loaded
  const { totalSpent, averageOrder } = useMemo(() => {
    if (!user) {
      return { totalSpent: 0, averageOrder: 0 };
    }
    const total = user.orders.reduce(
      (sum, order) => sum + (parseFloat(order.totalPrice as any) || 0),
      0,
    );
    const average = user.orders.length > 0 ? total / user.orders.length : 0;
    return { totalSpent: total, averageOrder: average };
  }, [user]);

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  // Prepare data for the bar chart (individual order spending)
  const barChartData = {
    labels: user.orders.map((order, index) => `Zamówienie ${index + 1}`),
    datasets: [
      {
        label: 'Kwota zamówienia (PLN)',
        data: user.orders.map((order) => order.totalPrice),
        backgroundColor: 'rgba(99, 102, 241, 0.6)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: 'Inter, sans-serif',
          },
        },
      },
      title: {
        display: true,
        text: 'Kwoty zamówień',
        font: {
          family: 'Inter, sans-serif',
          size: 16,
          weight: 500,
        },
        color: '#374151',
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#111827',
        bodyColor: '#374151',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        titleFont: {
          family: 'Inter, sans-serif',
        },
        bodyFont: {
          family: 'Inter, sans-serif',
        },
        callbacks: {
          label: function (context: any) {
            return `${context.raw.toFixed(2)} PLN`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: 'Inter, sans-serif',
          },
        },
      },
      y: {
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          font: {
            family: 'Inter, sans-serif',
          },
          callback: function (value: any) {
            return value + ' PLN';
          },
        },
      },
    },
  };

  // Prepare data for the line chart (cumulative spending over time)
  // Filter orders that have a valid createdAt date and sort them by date
  const ordersWithDate = user.orders.filter((order) => order.createdAt);
  const sortedOrders = [...ordersWithDate].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const cumulativeValues = sortedOrders.reduce<number[]>(
    (acc, order, index) => {
      const previousTotal = index > 0 ? acc[index - 1] : 0;
      acc.push(previousTotal + order.totalPrice);
      return acc;
    },
    [],
  );

  const lineChartData = {
    labels: sortedOrders.map((order) =>
      new Date(order.createdAt).toLocaleDateString(),
    ),
    datasets: [
      {
        label: 'Łączne wydatki (PLN)',
        data: cumulativeValues,
        fill: {
          target: 'origin',
          above: 'rgba(99, 102, 241, 0.1)',
        },
        borderColor: 'rgba(99, 102, 241, 1)',
        backgroundColor: 'rgba(99, 102, 241, 1)',
        pointBackgroundColor: 'rgba(99, 102, 241, 1)',
        pointBorderColor: '#fff',
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: 'Inter, sans-serif',
          },
        },
      },
      title: {
        display: true,
        text: 'Łączne wydatki w czasie',
        font: {
          family: 'Inter, sans-serif',
          size: 16,
          weight: 500,
        },
        color: '#374151',
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#111827',
        bodyColor: '#374151',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        titleFont: {
          family: 'Inter, sans-serif',
        },
        bodyFont: {
          family: 'Inter, sans-serif',
        },
        callbacks: {
          label: function (context: any) {
            return `${context.raw.toFixed(2)} PLN`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: 'Inter, sans-serif',
          },
        },
      },
      y: {
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          font: {
            family: 'Inter, sans-serif',
          },
          callback: function (value: any) {
            return value + ' PLN';
          },
        },
        beginAtZero: true,
      },
    },
  };

  // Funkcja zwracająca odpowiedni kolor dla statusu użytkownika
  const getUserStatusColor = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Active:
        return 'bg-green-50 text-green-700 border-green-200';
      case UserStatus.Inactive:
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <motion.div
      className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Przycisk powrotu */}
      <motion.button
        onClick={handleBackClick}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        variants={itemVariants}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <ArrowBack fontSize="small" />
        <span className="font-medium">Powrót</span>
      </motion.button>

      {/* Nagłówek z danymi użytkownika */}
      <motion.div className="mb-8" variants={itemVariants}>
        <div className="flex items-center gap-3 mb-4">
          <Person className="text-primary" fontSize="large" />
          <h1 className="text-2xl font-medium text-gray-900">
            {user.name} {user.surname}
          </h1>
          <span
            className={`ml-3 px-3 py-1 text-xs font-medium rounded-full ${getUserStatusColor(user.status)}`}
          >
            {translateUserStatus(user.status)}
          </span>
        </div>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Informacje użytkownika */}
        <motion.div className="md:col-span-1" variants={itemVariants}>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center">
                <Person className="text-gray-500 mr-2" fontSize="small" />
                <h3 className="text-base font-medium text-gray-800">
                  Dane użytkownika
                </h3>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <div className="flex items-center text-gray-500 mb-1 text-sm">
                  <Person fontSize="small" className="mr-2" />
                  <span>Imię i nazwisko</span>
                </div>
                <p className="font-medium">
                  {user.name} {user.surname}
                </p>
              </div>

              <div>
                <div className="flex items-center text-gray-500 mb-1 text-sm">
                  <Email fontSize="small" className="mr-2" />
                  <span>Email</span>
                </div>
                <p className="font-medium">{user.email}</p>
              </div>

              <div>
                <div className="flex items-center text-gray-500 mb-1 text-sm">
                  <WorkOutline fontSize="small" className="mr-2" />
                  <span>Rola użytkownika</span>
                </div>
                <p className="font-medium">{user.role}</p>
              </div>

              <div>
                <div className="flex items-center text-gray-500 mb-1 text-sm">
                  <VerifiedUser fontSize="small" className="mr-2" />
                  <span>Status</span>
                </div>
                <p className="font-medium">
                  {translateUserStatus(user.status)}
                </p>
              </div>
            </div>
          </div>

          {/* Podsumowanie wydatków */}
          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6"
            variants={itemVariants}
          >
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center">
                <Analytics className="text-gray-500 mr-2" fontSize="small" />
                <h3 className="text-base font-medium text-gray-800">
                  Podsumowanie zakupów
                </h3>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <p className="text-sm text-blue-700 mb-1">Łączne wydatki</p>
                  <p className="text-2xl font-bold text-blue-800">
                    {totalSpent.toFixed(2)} zł
                  </p>
                </div>

                <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                  <p className="text-sm text-green-700 mb-1">Średnia wartość</p>
                  <p className="text-2xl font-bold text-green-800">
                    {averageOrder.toFixed(2)} zł
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                  <p className="text-sm text-amber-700 mb-1">Liczba zamówień</p>
                  <p className="text-2xl font-bold text-amber-800">
                    {user.orders.length}
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                  <p className="text-sm text-purple-700 mb-1">Status konta</p>
                  <p className="text-xl font-bold text-purple-800">
                    {translateUserStatus(user.status)}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Wykresy i zamówienia */}
        <motion.div className="md:col-span-2" variants={itemVariants}>
          {/* Wykresy */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center">
                <Analytics className="text-gray-500 mr-2" fontSize="small" />
                <h3 className="text-base font-medium text-gray-800">
                  Analiza wydatków
                </h3>
              </div>
            </div>

            <div className="p-5 space-y-6">
              {user.orders.length > 0 ? (
                <>
                  <div className="h-60 sm:h-80">
                    <Bar data={barChartData} options={barChartOptions} />
                  </div>

                  {sortedOrders.length > 1 && (
                    <div className="h-60 sm:h-80 mt-6">
                      <Line data={lineChartData} options={lineChartOptions} />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-10">
                  <ShoppingBag
                    className="text-gray-300 mb-3"
                    style={{ fontSize: '3rem' }}
                  />
                  <p className="text-gray-500">
                    Brak danych do analizy - użytkownik nie złożył jeszcze
                    żadnych zamówień
                  </p>
                </div>
              )}
            </div>
          </div> */}

          {/* Lista zamówień */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ShoppingBag
                    className="text-gray-500 mr-2"
                    fontSize="small"
                  />
                  <h3 className="text-base font-medium text-gray-800">
                    Zamówienia użytkownika
                  </h3>
                </div>
                <span className="text-sm text-gray-500 px-2 py-1 bg-gray-100 rounded">
                  {user.orders.length}{' '}
                  {user.orders.length === 1
                    ? 'zamówienie'
                    : user.orders.length < 5
                      ? 'zamówienia'
                      : 'zamówień'}
                </span>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {user.orders.length === 0 ? (
                <div className="p-6 text-center">
                  <ShoppingBag
                    className="text-gray-300 mb-3"
                    style={{ fontSize: '2rem' }}
                  />
                  <p className="text-gray-500">
                    Użytkownik nie ma jeszcze żadnych zamówień
                  </p>
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto">
                  {user.orders.map((order) => (
                    <motion.div
                      key={order.id}
                      className="p-1"
                      whileHover={{
                        backgroundColor: 'rgba(249, 250, 251, 0.8)',
                      }}
                    >
                      <OrderCard
                        order={order}
                        url={`/admin/zamówienia/${order.id}`}
                      />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default User;
